<script setup lang="ts">
import type { Notification } from '@cockpit/shared';
import { computed, nextTick, onBeforeUnmount, onMounted, ref } from 'vue';
import { useRouter } from 'vue-router';
import { ApiError } from '../api';
import { BELL_LIMIT, bellBadge, bellLabel, notificationAge, notificationTarget } from '../notifications';
import { useNotificationsStore } from '../stores/notifications';
import { useToastStore } from '../stores/toast';

/**
 * Lot AJ : cloche de l'en-tête. Pastille des non lues ; au clic, les dernières notifications (titre, texte,
 * il y a combien de temps, non lues marquées). Une notification ouvre son écran et passe en lue ;
 * « Tout marquer comme lu » ; « Tout voir » mène à la page Notifications. Mise à jour en direct (SSE).
 * Clavier : Entrée/Espace ouvre, flèches haut/bas d'une notification à l'autre, Échap ferme.
 */
const emit = defineEmits<{ open: [] }>();
const store = useNotificationsStore();
const toast = useToastStore();
const router = useRouter();

const open = ref(false);
const root = ref<HTMLElement | null>(null);
const button = ref<HTMLButtonElement | null>(null);
const panel = ref<HTMLElement | null>(null);
/** Décalage horizontal qui garde le panneau dans l'écran (téléphone). */
const shift = ref(0);
const now = ref(new Date());

const latest = computed(() => store.items.slice(0, BELL_LIMIT));
const badge = computed(() => bellBadge(store.unread));
const label = computed(() => bellLabel(store.unread));

function errorMessage(err: unknown): string {
  return err instanceof ApiError ? err.message : 'Une erreur inattendue est survenue.';
}

function items(): HTMLElement[] {
  return [...(panel.value?.querySelectorAll<HTMLElement>('[data-bell-item]') ?? [])];
}

/** Garde le panneau entre les deux marges de 16 px de l'écran. */
function fitPanel(): void {
  shift.value = 0;
  void nextTick(() => {
    const rect = panel.value?.getBoundingClientRect();
    if (!rect) return;
    const margin = 16;
    const width = document.documentElement.clientWidth;
    if (rect.left < margin) shift.value = margin - rect.left;
    else if (rect.right > width - margin) shift.value = width - margin - rect.right;
  });
}

async function show(): Promise<void> {
  now.value = new Date();
  open.value = true;
  emit('open');
  fitPanel();
  await nextTick();
  (items()[0] ?? panel.value?.querySelector<HTMLElement>('button, a'))?.focus();
}

function close(returnFocus = false): void {
  if (!open.value) return;
  open.value = false;
  if (returnFocus) button.value?.focus();
}

function toggle(): void {
  if (open.value) close();
  else void show();
}

async function choose(notification: Notification): Promise<void> {
  const target = notificationTarget(notification.url);
  if (target) close();
  try {
    await store.markRead(notification.id);
  } catch (err) {
    toast.show(errorMessage(err));
  }
  if (target) await router.push(target);
}

async function markAll(): Promise<void> {
  try {
    await store.markAllRead();
  } catch (err) {
    toast.show(errorMessage(err));
  }
}

async function seeAll(): Promise<void> {
  close();
  await router.push('/notifications');
}

function onPanelKeydown(event: KeyboardEvent): void {
  if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
  const list = items();
  if (list.length === 0) return;
  event.preventDefault();
  const index = list.indexOf(document.activeElement as HTMLElement);
  const next =
    event.key === 'ArrowDown' ? (index + 1) % list.length : index <= 0 ? list.length - 1 : index - 1;
  list[next]?.focus();
}

function onDocumentClick(event: MouseEvent): void {
  if (open.value && root.value && !root.value.contains(event.target as Node)) close();
}
function onDocumentKeydown(event: KeyboardEvent): void {
  if (event.key === 'Escape' && open.value) close(true);
}
function onResize(): void {
  if (open.value) fitPanel();
}

onMounted(() => {
  store.start();
  document.addEventListener('click', onDocumentClick);
  document.addEventListener('keydown', onDocumentKeydown);
  window.addEventListener('resize', onResize);
});
onBeforeUnmount(() => {
  store.stop();
  document.removeEventListener('click', onDocumentClick);
  document.removeEventListener('keydown', onDocumentKeydown);
  window.removeEventListener('resize', onResize);
});
</script>

<template>
  <div ref="root" class="bell">
    <button
      ref="button"
      class="bell-btn"
      type="button"
      aria-haspopup="dialog"
      aria-controls="bell-panel"
      :aria-expanded="open ? 'true' : 'false'"
      :aria-label="label"
      :title="label"
      data-testid="bell-button"
      @click="toggle"
    >
      <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" focusable="false">
        <path
          d="M12 3a6 6 0 0 0-6 6v3.6l-1.6 2.7A1 1 0 0 0 5.3 17h13.4a1 1 0 0 0 .9-1.7L18 12.6V9a6 6 0 0 0-6-6Zm0 18a3 3 0 0 0 2.8-2H9.2a3 3 0 0 0 2.8 2Z"
          fill="currentColor"
        />
      </svg>
      <span v-if="badge" class="bell-count" data-testid="bell-count" aria-hidden="true">{{ badge }}</span>
    </button>
    <div
      v-if="open"
      id="bell-panel"
      ref="panel"
      class="bell-panel"
      role="dialog"
      aria-labelledby="bell-title"
      data-testid="bell-panel"
      :style="shift ? { transform: `translateX(${shift}px)` } : undefined"
      @keydown="onPanelKeydown"
    >
      <div class="bell-h">
        <h2 id="bell-title">Notifications</h2>
        <button
          v-if="store.unread > 0"
          class="btn sm"
          type="button"
          data-testid="bell-mark-all"
          @click="markAll"
        >
          Tout marquer comme lu
        </button>
      </div>
      <ul v-if="latest.length" class="bell-list" data-testid="bell-list">
        <li v-for="item in latest" :key="item.id">
          <button
            type="button"
            class="bell-item"
            :class="{ unread: !item.readAt }"
            data-bell-item
            :data-testid="`bell-item-${item.id}`"
            @click="choose(item)"
          >
            <span class="bell-dot" aria-hidden="true"></span>
            <span class="bell-text">
              <b>{{ item.title }}</b>
              <span class="bell-body">{{ item.body }}</span>
              <small>
                <span v-if="!item.readAt" class="sr-only">Non lue · </span
                >{{ notificationAge(item.createdAt, now) }}
              </small>
            </span>
          </button>
        </li>
      </ul>
      <p v-else class="bell-empty">
        {{ store.loaded ? 'Aucune notification pour l’instant.' : 'Chargement…' }}
      </p>
      <div class="bell-f">
        <button type="button" class="btn sm" data-testid="bell-see-all" @click="seeAll">Tout voir</button>
      </div>
    </div>
  </div>
</template>

<style scoped>
.bell {
  position: relative;
}
.bell-btn {
  position: relative;
  display: inline-grid;
  place-items: center;
  width: 36px;
  height: 36px;
  border: 1px solid var(--line);
  background: var(--panel);
  color: var(--ink);
  border-radius: 999px;
  cursor: pointer;
}
.bell-btn:hover,
.bell-btn[aria-expanded='true'] {
  background: var(--bg);
}
.bell-count {
  position: absolute;
  top: -5px;
  right: -6px;
  min-width: 18px;
  font-size: 11px;
  font-family: var(--f-mono);
  line-height: 18px;
  padding: 0 5px;
  border-radius: 999px;
  background: var(--bad);
  color: #fff;
  text-align: center;
  box-shadow: 0 0 0 2px var(--panel);
}
.bell-panel {
  position: absolute;
  right: 0;
  top: calc(100% + 8px);
  width: min(380px, calc(100vw - 32px));
  max-height: min(70vh, 560px);
  display: flex;
  flex-direction: column;
  background: var(--panel);
  border: 1px solid var(--line);
  border-radius: var(--radius);
  box-shadow: 0 12px 30px rgba(10, 20, 30, 0.16);
  z-index: 30;
}
.bell-h,
.bell-f {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 10px 12px;
}
.bell-h {
  border-bottom: 1px solid var(--line);
}
.bell-f {
  border-top: 1px solid var(--line);
  justify-content: flex-end;
}
.bell-h h2 {
  font-size: 15px;
  margin: 0;
}
.bell-list {
  list-style: none;
  margin: 0;
  padding: 4px;
  overflow-y: auto;
}
.bell-item {
  display: grid;
  grid-template-columns: 10px minmax(0, 1fr);
  gap: 8px;
  width: 100%;
  text-align: left;
  border: 0;
  background: none;
  padding: 9px 8px;
  border-radius: 8px;
  cursor: pointer;
  color: inherit;
  font: inherit;
}
.bell-item:hover,
.bell-item:focus-visible {
  background: var(--bg);
}
.bell-item.unread {
  background: var(--accent-soft);
}
.bell-item.unread:hover,
.bell-item.unread:focus-visible {
  background: color-mix(in srgb, var(--accent-soft) 70%, var(--bg));
}
.bell-dot {
  width: 8px;
  height: 8px;
  margin-top: 6px;
  border-radius: 50%;
}
.bell-item.unread .bell-dot {
  background: var(--accent);
}
.bell-text {
  display: grid;
  gap: 2px;
  min-width: 0;
  font-size: 13.5px;
}
.bell-text b {
  font-weight: 600;
}
.bell-item:not(.unread) .bell-text b {
  font-weight: 500;
}
.bell-body {
  white-space: pre-line;
  overflow-wrap: anywhere;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.bell-text small {
  color: var(--muted);
}
.bell-empty {
  margin: 0;
  padding: 18px 12px;
  color: var(--muted);
  font-size: 13.5px;
}
</style>
